import { Sprite, Texture, Container, TilingSprite, Rectangle, Graphics, Assets } from 'pixi.js';
import { craftingApi, foodApi, villageApi } from '../../game-api';
import { isWalkableBuildingKind } from '../../map/decor-kinds';
import { TILE, WATER_KINDS, BIOME_VOLCANO, applyPlantedSaplings, saplingGrown, pickSpawn, type World, type Decor } from '../../map/world';
import { TREE_KINDS, type ShoreKey } from '../../map/textures';
import { DECOR_ANIM, FOUNTAIN_ANIM_SCALE, animFps, animStartFrame, hasDecorAnim } from '../../map/decor-anim';
import { toast } from '../../dom';
import { attachCamera } from '../../map/camera';
import { createPlayer, FOOT_OFFSET } from '../../map/player';
import { findRoute, nearestOpen, type NavGrid } from '../../map/nav';
import { createFauna, loadFaunaTextures } from '../../map/fauna';
import { createVillagers, PLOT_NODE, PEN_ANIMAL_NODE } from '../../map/villagers';
import { refreshLivestock, findPenAnimal } from './livestock';
import { createDrops } from '../../map/drops';
import { createMobs, loadMobTextures } from '../../map/mobs';
import { createEffects } from './effects';
import { playSfx, playSfxAt } from '../../audio/audio';
import { createRiding } from '../../map/riding';
import { loadHorseState } from '../../map/horse-state';
import { createCampfire } from '../../map/campfire';
import { TENT_SAFE_RADIUS_TILES, TENT_SCALE, TENT_ANCHOR_Y } from './placement';
import { createHeldTorch } from '../../map/lights';
import { applyItemTexture, itemTexture, repaintGear } from './context';
import { loadAppearance } from '../../character/appearance-store';
import { defaultAppearance, type Appearance } from '../../character/compositor';
import {
  AXE_CURSOR, PICK_CURSOR, BUCKET, CULL_MARGIN, FADE_ALPHA, GATHER_KINDS, PATH_CACHE_MAX, PX, TALL_KINDS,
  bkey, clampPlayer, decorScale, decorTexture, isCleared, isFelled, isGathered, isMined, stumpFor, syncHeld,
  type FarmCtx, type GearTiers, type AnimWater,
} from './context';
import { paintPlots } from './plots';
import { requestOperate, autoSelectFor } from './input';
import { loadLastPosition } from '../../map/last-position';
import { relocateRegrown, type RelocateWorld } from '../../map/regrow-relocate';
import { armourLayerFile } from '../../ui/armor-art';
import { armourPaintColor } from '../../map/ores';

// ─── Scene ────────────────────────────────────────────────────────────────
// One tiling sprite per horizontal run of same-type land, not per tile —
// collapses ~130k land tiles into a few thousand sprites on the large map.
function terrainRun(c: FarmCtx, t: World['terrain'][number], len: number, tx: number, ty: number, volcano = false): Container {
  const texture = t === 'till' ? c.tex!.ground.soil
    : volcano ? (c.tex!.ground.volcano_field ?? c.tex!.ground.grass)
    : (c.tex!.ground.grass_field ?? c.tex!.ground.grass);
  const ts = new TilingSprite({ texture, width: len * TILE, height: TILE });
  ts.tileScale.set(TILE / 16);   // each 16px sub-cell = 1 tile; grass_field repeats every 4 tiles
  // Anchor the 4×4 grass texture to the GLOBAL tile grid so the detail cells land
  // on fixed world tiles (not restart per run) and vary by row as well as column.
  ts.tilePosition.set(-(((tx % 4) + 4) % 4) * TILE, -(((ty % 4) + 4) % 4) * TILE);
  return ts;
}

// Which waterline tiles a shore-water cell is built from, given its land
// neighbours. Whole 16px tiles only: the pack draws the waterline as a
// vertical run down the full tile — lawn, then the stone lip, then the
// animated foam — so an 8px quadrant crop keeps the lawn and discards both the
// lip and every animated pixel. Tiles stack safely because the flat sea is
// punched out of them (see scripts/build-shore-tiles.py).
function shoreKeys(
  n: boolean, s: boolean, w: boolean, e: boolean,
  nw: boolean, ne: boolean, sw: boolean, se: boolean,
): ShoreKey[] {
  const keys: ShoreKey[] = [];
  const sides = (n ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0) + (e ? 1 : 0);
  const edges = () => {
    if (n) keys.push('t');
    if (s) keys.push('b');
    if (w) keys.push('l');
    if (e) keys.push('r');
  };
  // Land on three sides is a 1-wide channel head, which the pack has no single
  // tile for — stack its edges so each bank keeps its own lip.
  // §2.6: the rounded corner tile is only correct for a TRUE convex corner, where
  // the DIAGONAL is also land (land wraps the water's corner). When two adjacent
  // sides are land but the diagonal is water (an inner gap), the convex tile
  // mis-stitches — fall back to stacking the two edge lips, which is always valid.
  if (sides >= 3) edges();
  else if (n && w) { if (nw) keys.push('tl'); else edges(); }
  else if (n && e) { if (ne) keys.push('tr'); else edges(); }
  else if (s && w) { if (sw) keys.push('bl'); else edges(); }
  else if (s && e) { if (se) keys.push('br'); else edges(); }
  else edges();                            // one side, or a 1-wide strait (N+S / W+E)
  // Land touching only at a corner gets the concave island-corner tile.
  if (nw && !n && !w) keys.push('ctl');
  if (ne && !n && !e) keys.push('ctr');
  if (sw && !s && !w) keys.push('cbl');
  if (se && !s && !e) keys.push('cbr');
  return keys;
}

/**
 * Is this decor a planted acorn that has not become a tree yet?
 *
 * Read from `world.saplings` (the deadline) every time rather than from a cached
 * flag: the deadline is hours away, the tab stays open, and a flag decided at load
 * would leave a seedling un-choppable long after it had grown.
 */
export const isYoungSapling = (c: FarmCtx, d: Decor): boolean => {
  const readyAt = c.world?.saplings?.get(d.id);
  return readyAt !== undefined && !saplingGrown(readyAt);
};

/** ms left before a planted acorn is a fellable tree (0 = grown / not a sapling). */
export const saplingRemainingMs = (c: FarmCtx, d: Decor): number =>
  Math.max(0, (c.world?.saplings?.get(d.id) ?? 0) - Date.now());

export function buildScene(c: FarmCtx) {
  if (!c.app || !c.world || !c.tex) return;
  // The player's own trees go onto the decor list BEFORE anything buckets or
  // culls it — a sapling added afterwards would land in no bucket and be invisible
  // until the next full rebuild. Reload safety lives here: the map is re-derived
  // from the seed and remembers nothing, so this snapshot is the only memory of it.
  applyPlantedSaplings(c.world, c.farm?.saplings ?? []);
  c.cam?.destroy();
  c.scenery?.destroy({ children: true });
  c.waterFxLayer?.destroy({ children: true });
  c.fxLayer?.destroy({ children: true });
  c.animWater = [];
  c.app.stage.removeChildren();
  // The ghost lived in the old cam.world and died with it; keeping the stale
  // handle would make updateBuildGhost skip its create branch forever.
  c.buildGhost = null;
  const W = c.world.cols * TILE, H = c.world.rows * TILE;
  const cam = attachCamera(c.app, W, H);
  c.cam = cam;
  // The stage was just wiped, which orphans the lighting layer — it lives on the
  // stage (screen space) rather than in cam.world, so rebuilding the scene drops
  // it. Re-attach, on top of the freshly-added camera.
  if (c.dayNight) c.app.stage.addChild(c.dayNight.layer);

  // Calm still sea: one big tiling sprite under everything; land draws on top.
  // Deliberately NOT animated — cycling the tiled base runs the same splash in
  // every cell at once. Movement is scattered over it (see world gen water FX).
  c.waterLayer = new TilingSprite({ texture: c.tex.water.still[0], width: W, height: H });
  c.waterLayer.tileScale.set(TILE / 16); // 16px water tile → one per game tile
  cam.world.addChild(c.waterLayer);

  // Shore water life: ripples/droplets, fish, stones, reeds — above the sea,
  // below the land. Bounded to the coastline (see world gen), so no culling.
  const waterFxLayer = new Container();
  c.waterFxLayer = waterFxLayer;
  cam.world.addChild(waterFxLayer);
  c.animWater = [];
  const world = c.world;
  // Coastline + terrain are built PER CHUNK on demand by cullTerrain() as the player
  // moves — the whole 683×464 map is never resident. Only the sparse water decor is
  // placed eagerly here (it's cheap and rarely on screen at once).
  for (const d of world.decor) {
    if (WATER_KINDS.has(d.kind)) waterFxLayer.addChild(makeWaterSprite(c, d));
  }
  c.fauna?.destroy();
  c.fauna = null;
  c.livestock?.destroy();
  c.livestock = null;

  // Ground-terrain chunks are added here; each chunk is its own cached render group.
  const scenery = new Container();
  c.scenery = scenery;
  scenery.sortableChildren = true;
  for (const v of c.terrainChunks.values()) { v.ground.destroy(); v.shore.destroy(); }
  c.terrainChunks.clear();
  // Decor is BUCKETED as data (not instantiated) so both tall decor (trees) and
  // small scatter (grass/flowers) are viewport-culled — only what's near the player
  // ever becomes a Sprite. On the full 683×464 map that keeps ~tens of thousands of
  // scatter sprites out of memory.
  c.tallBuckets.clear();
  c.smallBuckets.clear();
  for (const d of world.decor) {
    if (WATER_KINDS.has(d.kind)) continue;
    const k = bkey(d.tx, d.ty);
    const buckets = TALL_KINDS.has(d.kind) ? c.tallBuckets : c.smallBuckets;
    (buckets.get(k) ?? buckets.set(k, []).get(k)!).push(d);
  }
  scenery.enableRenderGroup();   // terrain only now — small decor moved to its own culled layer
  cam.world.addChild(scenery);

  // Small scatter renders just above the terrain and below plots/entities (the
  // player walks over it). Populated per-frame by cullSmall().
  const smallLayer = new Container();
  c.smallLayer = smallLayer;
  cam.world.addChild(smallLayer);

  const plotLayer = new Container();
  c.plotLayer = plotLayer;
  plotLayer.sortableChildren = true;
  cam.world.addChild(plotLayer);

  // Entity layer: tall decor (culled) + the player, y-sorted every frame.
  const entityLayer = new Container();
  c.entityLayer = entityLayer;
  entityLayer.sortableChildren = true;
  cam.world.addChild(entityLayer);
  c.activeTall.clear();
  c.activeSmall.clear();   // old scatter sprites died with the previous cam.world
  // Animals join the y-sorted entity layer so a cow correctly walks behind a
  // tree and in front of the one below it, like the hero does.
  //
  // Gated only on the herd itself. It used to also require faunaStats to be
  // populated, which coupled ~1000 seed-generated animals to an unrelated HTTP
  // call whose error was swallowed — one transient failure emptied the world with
  // no log. Stats now fall back to DEFAULT_STATS, so a failed fetch costs stale
  // tuning, not the animals.
  if (world.animals.length) {
    void loadFaunaTextures(world.animals).then((ftex) => {
      if (!c.alive || !c.entityLayer) return;
      // Mount the herd INTO the y-sorted entity layer so animals sort with trees/rocks
      // by feet-Y (no longer stuck behind every decor sprite).
      c.fauna = createFauna(c.world!.animals, ftex, c.faunaStats, c.faunaTuning, c.entityLayer);
    }).catch((e) => console.error('[fauna] load failed:', e));
  }

  // Village life: the summoned villagers wander near their homes in the same
  // y-sorted entity layer (best-effort — no villagers is fine).
  mountVillagers(c);

  // Owned pen stock, drawn by its own Fauna instance (farm/livestock.ts). Fetched
  // rather than derived from the world seed — these animals are server rows.
  void villageApi.husbandry()
    .then((h) => refreshLivestock(c, h.animals))
    .catch(() => { /* pens are best-effort ambience too */ });

  // Ground drops: felled logs, mined ore, harvested produce, animal loot. Its own
  // layer inside the entity layer so a dropped item y-sorts against the hero and
  // the trees, like an animal does.
  c.drops?.destroy();
  c.drops = createDrops((itemId) => itemTexture(c, itemId), (s, itemId) => applyItemTexture(c, s, itemId));
  c.entityLayer.addChild(c.drops.layer);

  // Night mobs: their own y-sorted layer in the entity layer. Loaded lazily (only
  // the species this player may face), spawned by the night ticker in farm.ts.
  c.mobs?.destroy();
  c.mobs = null;

  // Riding layer: the tamed horse (if any) stands in the world, in the entity
  // layer so it y-sorts. Idle frames reuse the animal horse sprites by type.
  c.riding?.destroy();
  // Horse variants are named work/racing/heavy, matching the tamed type. Frames are
  // loaded through Assets inside the riding layer (see riding.ts) — the old code
  // used `Texture.from(url)`, which in Pixi v8 is only a cache lookup and returned
  // undefined here, so a player who already owned a horse got a white box.
  c.riding = createRiding(c.entityLayer);
  // (the horse itself is placed after buildCollision, below — placing it needs a
  // current collision grid to find a walkable spot)
  // §7: combat effects (arrows / slashes / sparks / damage numbers), world space.
  c.effects?.destroy();
  c.effects = createEffects();
  cam.world.addChild(c.effects.layer);

  if (c.mobStats.length) {
    void loadMobTextures(c.mobStats.map((m) => m.id)).then((mtex) => {
      if (!c.alive || !c.entityLayer) return;
      c.mobs = createMobs(mtex, {
        // A melee hit lands on the player: spark + damage number + a slash toward him.
        onHitPlayer: (dmg) => {
          c.onMobHit?.(dmg);
          if (c.effects && c.player) {
            c.effects.hit(c.player.x, c.player.y - FOOT_OFFSET, dmg);
            c.effects.slash(c.player.x, c.player.y - FOOT_OFFSET, c.player.dir);
          }
        },
        // Mob cues are deliberately STINGY. A night pack is a dozen entities each
        // noticing, swinging and dying within a second or two of each other; giving
        // every one of those events its own sound is what turned a fight into mush.
        // One at a time, well spaced, and only the events the player must react to.
        onDeath: (m) => { playSfxAt('mob_death', m.x, m.y, { cooldownMs: 400, maxConcurrent: 1 }); c.onMobDeath?.(m); },
        // A mob noticing you is the one warning the game gives at night, and it has
        // to be positional: the growl tells you which direction it came from. Once
        // per second at most — the pack aggros together, but you only need telling once.
        onAggro: (m) => playSfxAt('mob_aggro', m.x, m.y, { cooldownMs: 1200, maxConcurrent: 1 }),
        // The mob's own swing is intentionally silent: the player's sword is the
        // sound of a fight, and the hit already lands as a damage number and a
        // flash. Adding a grunt per attacker per swing was the overlay complaint.
        onAttack: undefined,
        onCallAdds: (m, n) => { playSfxAt('mob_aggro', m.x, m.y, { cooldownMs: 1200, maxConcurrent: 1 }); c.onMobCallAdds?.(m, n); },
        // §7: a ranged mob released — fly a visible arrow to the player; damage
        // (the same onMobHit path) applies on ARRIVAL, not on fire.
        onFire: (m, dmg) => {
          playSfxAt('bow_shot', m.x, m.y);
          if (!c.effects || !c.player) return;
          const px = c.player.x, py = c.player.y - FOOT_OFFSET;
          c.effects.arrow(m.x, m.y - TILE * 0.6, px, py, () => {
            playSfx('arrow_hit');            // the arrow ARRIVES — heard at the hero
            c.onMobHit?.(dmg);
            c.effects?.hit(px, py, dmg);
          });
        },
      }, c.mobTuning, c.entityLayer);   // mount in the y-sorted entity layer (sort with decor)
    }).catch((e) => console.error('[mobs] load failed:', e));
  }

  // Chop particles + axe hint + progress ring draw on top of everything.
  const fxLayer = new Container();
  c.fxLayer = fxLayer;
  cam.world.addChild(fxLayer);
  // White square bracket (UI pack) that frames the lower part of a hovered tree.
  // (texture preloaded in init so its size is known when we scale it)
  const selector = new Sprite(Texture.from('/sprites/ui/tree_select.png'));
  c.selector = selector;
  selector.anchor.set(0.5, 0.5);
  selector.visible = false;
  selector.eventMode = 'none';
  fxLayer.addChild(selector);
  // §3: Tool/build target frame — the UI-pack corner bracket, tinted white/green/
  // red per state, breathing + lerping to the hovered tile (see updateToolFrame).
  //
  // `tile_select.png` is the UI pack's four ROUNDED corner pips (build-ui-selectors.py).
  // It replaced `tree_select.png`, whose arms ran inward and read as four lines
  // crossing the tile rather than a frame around it.
  // c.tex.fx.tile_select, not Texture.from(url): an unloaded URL yields an EMPTY
  // texture in Pixi 8, which is why this bracket never appeared on screen.
  const tileFrame = new Sprite(c.tex!.fx.tile_select);
  c.tileFrame = tileFrame;
  tileFrame.anchor.set(0.5, 0.5);
  tileFrame.visible = false;
  tileFrame.eventMode = 'none';
  tileFrame.zIndex = 1e6;
  fxLayer.addChild(tileFrame);
  for (const p of c.particles) p.s.destroy();
  c.particles = [];
  c.cutting = null;   // its ring/particles lived in the old fxLayer (now destroyed)

  buildCollision(c);
  // Place the tamed horse if the player owns one. Position isn't persisted
  // server-side (the server has no map), so it starts near the farm and the player
  // walks over to mount it. Snap to the nearest WALKABLE tile — the raw spot east of the
  // plots is often a tree/rock/water, which stranded the horse inside an obstacle
  // the hero could never walk up to and mount.
  if (c.horseStatus?.horse && c.riding) {
    const hinfo = c.horseStatus.types.find((t) => t.id === c.horseStatus!.horse);
    if (hinfo) {
      // Where the player LEFT it, if we remember. buildScene destroys and rebuilds
      // the riding layer, and that happens on every tab switch — so without this
      // the horse teleported back to the farm and the hero was put on foot, in the
      // middle of a ride. See map/horse-state.ts.
      const saved = loadHorseState(c.seed);
      const hwx = saved ? saved.tx : world.farm.tx + world.farm.cols + 1;
      const hwy = saved ? saved.ty : world.farm.ty;
      const open = nearestOpenTile(c, hwx, hwy);
      c.riding.setHorse(hinfo.id, hinfo, open?.[0] ?? hwx, open?.[1] ?? hwy);
      // Back in the saddle. Deferred because the hero does not exist yet —
      // ensurePlayer runs at the end of this function — and mounting is a
      // question about where the hero is standing.
      if (saved?.mounted) c.remountPending = true;
    }
  }
  const f = world.farm;
  cam.centerOn((f.tx + f.cols / 2) * TILE, (f.ty + f.rows / 2) * TILE);
  paintPlots(c);
  void ensurePlayer(c);
  void ensureCampfire(c);
  void ensureTent(c);
}

/** The tile just beside spawn where a newly-built campfire is placed. */
export function spawnAdjacentTile(c: FarmCtx): { tx: number; ty: number } {
  const f = c.world!.farm;
  return { tx: f.tx + Math.floor(f.cols / 2) + 2, ty: f.ty + f.rows + 1 };
}

/**
 * Place the built campfire into the (re)built scene. Re-attaches an existing
 * handle after a world rebuild, or fetches its position once and creates it.
 * Its animation joins the anim-water list the ticker already drives.
 */
export async function ensureCampfire(c: FarmCtx) {
  if (!c.entityLayer) return;
  if (c.campfire) {                                  // world rebuilt — re-attach + re-arm anim
    c.entityLayer.addChild(c.campfire.sprite);
    if (!c.animWater.includes(c.campfire.anim)) c.animWater.push(c.campfire.anim);
    return;
  }
  const status = await foodApi.status().catch(() => null);
  if (!status?.campfire || !c.alive || !c.entityLayer) return;
  c.campfire = await createCampfire(status.campfire.tx, status.campfire.ty);
  if (!c.alive || !c.entityLayer) { c.campfire.sprite.destroy(); c.campfire = null; return; }
  c.entityLayer.addChild(c.campfire.sprite);
  c.animWater.push(c.campfire.anim);
  armCampfireClick(c, c.campfire.sprite);
}

/**
 * Put the tent on the map, with the ground it keeps safe drawn under it.
 *
 * The tent existed for a long time as two numbers on the farm row (tent_tx/tent_ty,
 * the respawn point) and nothing else — no sprite, so the safest ground on the map
 * was invisible and the thing the player is told to move had nothing to click. A
 * safe zone you cannot see is not a mechanic, it is a rumour.
 *
 * Drawn as ONE sprite plus a translucent ring at the server's radius. The ring goes
 * in the plot layer, beneath everything that walks, so it reads as ground rather
 * than as a bubble hanging over the world.
 */
/**
 * Tent render scale. See the note at the sprite below: the tent art is a 48x96
 * building cell, not the 16px-native art TILE/16 assumes.
 */
// TENT_SCALE / TENT_ANCHOR_Y live in placement.ts so the build ghost draws the
// tent at exactly the size and anchor it will be pitched at — see the note there.

export async function ensureTent(c: FarmCtx) {
  // Clear whatever is on screen first: this runs again on every world rebuild and
  // after every pitch, and a tent that is drawn twice is a tent the player can pick
  // up twice.
  c.tentSprite?.destroy();
  c.tentSprite = null;
  c.tentRing?.destroy();
  c.tentRing = null;
  if (!c.entityLayer || !c.plotLayer) return;

  const t = c.tent;
  // Stowed = in the bag, not on the ground. `tx`/`ty` survive stowing (respawn still
  // needs them), so `pitched` is the only honest test for "is it standing here".
  if (!t?.pitched || t.tx == null || t.ty == null) return;

  const ring = new Graphics();
  const r = (t.safeRadius ?? TENT_SAFE_RADIUS_TILES) * TILE;
  ring.ellipse((t.tx + 0.5) * TILE, (t.ty + 0.9) * TILE, r, r * 0.6)
    .fill({ color: 0x8fd6a0, alpha: 0.10 })
    .stroke({ color: 0x8fd6a0, width: 2, alpha: 0.35 });
  c.plotLayer.addChild(ring);
  c.tentRing = ring;

  const tex = await Assets.load('/sprites/props/tent_small.png').catch(() => null);
  // The await above is a frame boundary: the view can be torn down or the world
  // rebuilt while it is in flight, and attaching to a dead layer throws.
  if (!tex || !c.alive || !c.entityLayer) { ring.destroy(); c.tentRing = null; return; }
  const s = new Sprite(tex as Texture);
  s.anchor.set(0.5, TENT_ANCHOR_Y);
  // TENT_SCALE, not the usual TILE/16.
  //
  // Every other map sprite is 16px-native pack art, so TILE/16 sizes it correctly.
  // The tent is not: it is a 48x96 BUILDING cell (~42x69 of actual ink), so the
  // standard scale drew it three tiles wide and six tall — taller than the
  // farmhouse and about twice the hero. It is a one-person camp, so it should read
  // as slightly bigger than the hero and no more.
  //
  // 1.15 put the ink at roughly 1.0 x 1.65 tiles, which read as too SMALL to be the
  // landmark you are told to walk back to — the owner asked for it half again as
  // big, so 1.15 x 1.5 = 1.725. That is ~1.5 x 2.5 tiles of ink: taller than the
  // hero and clearly a shelter, while still sitting well inside its 6-tile safe
  // ring (the 2.6 x 4.3 tiles the raw TILE/16 scale gave is what was far too big).
  s.scale.set(TENT_SCALE);
  s.x = (t.tx + 0.5) * TILE;
  s.y = (t.ty + TENT_ANCHOR_Y) * TILE;
  s.zIndex = s.y;
  s.eventMode = 'static';
  s.cursor = 'pointer';
  s.on('pointertap', (e) => {
    e.stopPropagation();
    // A camera drag ends in a tap on whatever is under the finger, and a click while
    // placing something belongs to the placement.
    if (c.cam?.dragged || c.buildMode) return;
    void import('./building-menu').then((m) => m.openTentMenu(c, c.tent!));
  });
  c.entityLayer.addChild(s);
  c.tentSprite = s;
}

/**
 * A14: cooking is done AT the campfire — click it to open the cooking bench
 * (no footer button). Dynamic import of ./food keeps this out of the static
 * import cycle (food.ts already imports scene.ts).
 */
export function armCampfireClick(c: FarmCtx, sprite: Sprite) {
  sprite.eventMode = 'static';
  sprite.cursor = 'pointer';
  sprite.on('pointertap', (e) => {
    e.stopPropagation();
    if (c.cam?.dragged || c.buildMode) return;
    void import('./food').then((m) => m.openFood(c));
  });
}

// Spawn the walking avatar once (re-parent it if the world is rebuilt).
export async function ensurePlayer(c: FarmCtx) {
  if (!c.entityLayer || !c.world) return;
  if (c.player) { c.entityLayer.addChild(c.player.sprite); return; }
  if (c.spawningPlayer) return;
  c.spawningPlayer = true;
  try {
    c.baseLook = loadAppearance(c.seed) ?? defaultAppearance();
    // Resume where the player actually was, falling back to the farm's doorstep.
    // Switching tabs rebuilds this view, so without the memory a player who walked
    // out to a seam was silently teleported home every time they checked the Shop.
    // The saved point is still clamped and unstuck below, because a building may
    // have been placed on it while they were away.
    const saved = loadLastPosition(c.seed);
    // The fallback used to be the doorstep tile straight off `f`, which is only
    // guaranteed to exist — not to be land. On a seed whose farm backs onto the
    // pond it is open water, and a first-time player arrived in the map swimming.
    // pickSpawn walks out from that same doorstep to the first tile with room for
    // a hero and his tent on it (map/world.ts).
    const start = saved ? null : pickSpawn(c.world, c.blockedGrid);
    const sx = saved ? saved.x : (start!.tx + 0.5) * TILE;
    const sy = saved ? saved.y : (start!.ty + 0.5) * TILE + FOOT_OFFSET;
    // Ask for the real tiers BEFORE building the hero.
    //
    // `c.gearTiers` starts as a placeholder ({sword:1, axe:1, …} — context.ts), and
    // the hero used to be built from it and then rebuilt by a fire-and-forget
    // refreshGear. So entering the game showed wood/copper gear that only became
    // mythril once something else called syncHeld — owner: "when I enter the game
    // I have copper ones or iron, but after clicking any item it shows me
    // mythril." One awaited request removes the wrong first frame entirely; if it
    // fails we fall back to the placeholder exactly as before.
    c.gearTiers = (await fetchGearTiers()) ?? c.gearTiers;
    // ...and repaint the BAR with them. Awaiting the tiers fixed the HERO, but the
    // hotbar had already been painted from the placeholder while this request was
    // in flight, so every tool still showed as Wood until an unrelated click
    // repainted it.
    repaintGear(c);
    const p = await createPlayer(gearedLook(c.baseLook, c.gearTiers), ...clampPlayer(c, sx, sy));
    if (!c.alive || !c.entityLayer) { p.destroy(); return; }
    c.player = p;
    c.entityLayer.addChild(p.sprite);
    c.cam?.centerOn(p.x, p.y);   // start looking at the hero (we no longer follow every frame)
    syncHeld(c);          // show the selected tool/weapon in hand (§1)
    attachHeldTorch(c);   // (re)build the carried-torch layer on the new hero
    // NOT refreshGear() any more: the tiers were fetched above and the hero was
    // built from them, so re-fetching would only rebuild an identical hero.

    // The grid was built before the hero existed, so the load-time rescue has to
    // run here: a player who logged out stuck inside a building spawns free.
    unstickPlayer(c);

    const overlay = document.getElementById('farm-loading');
    if (overlay) {
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 500);
    }
  } finally { c.spawningPlayer = false; }
}

/**
 * The carried torch is a layer parented to the hero's sprite, so every time the
 * hero is rebuilt (spawn, armor change) it has to be rebuilt with him — otherwise
 * the torch silently goes out the first time you craft a helmet.
 */
export function attachHeldTorch(c: FarmCtx) {
  c.heldTorch?.destroy();
  c.heldTorch = c.player ? createHeldTorch(c.player) : null;
  if (c.torchLit) c.heldTorch?.setLit(true);
}

/**
 * Armour art (tier → plate colour, light vs heavy helm, layer file) now lives in
 * ui/armor-art.ts and is re-exported here so the bag and the anvil can ask the
 * SAME function what the hero is wearing without importing this Pixi module. The
 * tables used to live here and be duplicated in scripts/build-armor-icons.py, so
 * a heavy helm on the hero could still be a light helm in the panels.
 */
export { plateFor, helmetFileFor, HEAVY_HELM_FROM_TIER, armourIsPainted } from '../../ui/armor-art';

// Equipped armor shows on the hero: swap the paper-doll's headgear/chest/legs
// to the crafted Plate layers (Iron at tier 1, Gold at tier 2).
export function gearedLook(base: Appearance, t: GearTiers): Appearance {
  const g: Appearance = { ...base };
  // armourLayerFile is what the bag and anvil icons are derived from, so dressing
  // the hero through it is what makes the three renders unable to disagree.
  //
  // `color` is the compositor's garment pass — a luminance-preserving remap, not a
  // hue rotation — so the tiers past the pack's four plate metals wear the plain
  // Iron plate in their own ore colour with its shading intact. Null for every
  // tier the pack DOES draw: those already carry their metal in the art, and
  // painting them would fight it.
  const paint = armourPaintColor;
  const helm = armourLayerFile('helmet', t.helmet);
  if (helm) {
    g.headgear = { file: helm, color: paint(t.helmet) };
    g.hair = { file: null, color: null };   // a helmet fully covers the head — hide the hair under it
  }
  const chest = armourLayerFile('chest', t.chest);
  if (chest) g.shirt = { file: chest, color: paint(t.chest) };
  const legs = armourLayerFile('legs', t.legs);
  if (legs) g.pants = { file: legs, color: paint(t.legs) };
  return g;
}

/**
 * The player's REAL gear tiers, from the server. Null if it could not be asked.
 *
 * Split out of refreshGear so the hero can be BUILT from the truth instead of
 * being built from a placeholder and corrected afterwards — see ensurePlayer.
 */
export async function fetchGearTiers(): Promise<GearTiers | null> {
  try {
    const st = await craftingApi.status();
    return {
      helmet: st.tiers.helmet, chest: st.tiers.chest, legs: st.tiers.legs,
      sword: st.tiers.sword, axe: st.tiers.axe, pickaxe: st.tiers.pickaxe, hoe: st.tiers.hoe,
    };
  } catch { return null; }
}

export async function refreshGear(c: FarmCtx) {
  if (!c.baseLook) return;
  const tiers = await fetchGearTiers();
  if (!tiers) return;
  c.gearTiers = tiers;
  // The bar shows the tier too — icons and badges — and it is the part the player
  // actually reads. Repainted on BOTH exits below, not just the rebuild path.
  repaintGear(c);
  // The tiers are now right even if the hero cannot be rebuilt this instant, so
  // the HELD tool's tier tint has to be re-applied on that path too. Skipping it
  // is how a mythril sword kept drawing as copper until some unrelated click
  // happened to call syncHeld again.
  if (!c.player || !c.entityLayer) { syncHeld(c); return; }
  const { x, y } = c.player;
  const np = await createPlayer(gearedLook(c.baseLook, c.gearTiers), x, y);
  if (!c.alive || !c.entityLayer) { np.destroy(); return; }
  const old = c.player; c.player = np;
  c.entityLayer.addChild(np.sprite);
  old.destroy();
  attachHeldTorch(c);   // the torch layer belonged to the old hero — move it across
  // The hero was REBUILT, so the held tool and its tier tint went with the old
  // one. Re-sync both — this is also what repaints a freshly-crafted Iron axe in
  // the hand the moment the craft lands.
  syncHeld(c);
}

// Impassable tiles: only genuine hard obstacles — the sea and the solid base of
// a mountain. Trees, hillocks and boulders are walk-through, so the player never
// hits an invisible wall in what looks like open ground. Footprints are aligned
// tight to each sprite's base tile (anchor is bottom-centre at tile d.ty).
/**
 * How big each multi-tile structure is actually DRAWN, in tiles (width x height).
 *
 * Source: the baked art's pixel size times `buildingScale(kind)` from plots.ts,
 * divided by TILE. The numbers are frozen here rather than computed because
 * plots.ts imports this module (a live import would close an initialisation
 * cycle) — `building-footprint.test.ts` recomputes them from `buildingScale` and
 * fails if the art scale is ever re-tuned without revisiting the walls.
 *
 * Only kinds drawn wider or taller than one tile need an entry; everything else
 * falls through to the single anchor tile.
 */
export const BUILDING_DRAWN_TILES: Record<string, [number, number]> = {
  hut: [2.84, 2.52],          // 90x80 art
  house: [2.21, 3.59],        // 70x114
  farmhouse: [3.21, 3.69],    // 102x117
  barn: [3.21, 3.69],         // 102x117
  lumber_hut: [2.21, 2.93],   // 70x93
  coop: [1.70, 3.62],         // 54x115
  mine_shaft: [1.81, 2.32],   // 32x41, boosted x1.8
  forage_hut: [2.17, 1.80],   // 46x38, boosted x1.5
};

/**
 * Above this drawn height a structure has a storey under its roof, so its walls
 * cover TWO rows rather than one. Below it (the hut's low cottage, the mine
 * shaft's cave mouth) the art is mostly roof and one row of wall is what is
 * really there. The house's long-standing 3x2 and the hut's 3x1 both fall out of
 * this rule unchanged — it was derived to reproduce them.
 */
const TWO_ROW_HEIGHT_TILES = 2.6;

/**
 * The tiles a placed building makes IMPASSABLE — the single source of truth for
 * both the collision grid and the "would this trap the hero?" check in build mode.
 *
 * Was: house 3x2, hut 3x1, and EVERY other kind exactly one tile. That last case
 * is what let the owner walk clean through the farmhouse: a farmhouse is drawn
 * 3.2 tiles wide and 3.7 tall but blocked a single tile, so its painted walls
 * were decoration and only the doorstep stopped you. The same was true of the
 * barn, the coop, the lumber hut and the forager hut.
 *
 * A column counts as wall when the sprite covers at least HALF of it (the sprite
 * is centred on its tile and anchored at the tile's bottom edge, so the rows grow
 * northward from `ty`). Half a tile is the honest threshold: a sliver of overhang
 * is roof, and blocking on it would put an invisible wall in open grass.
 */
export function solidBuildingTiles(kind: string, tx: number, ty: number): [number, number][] {
  if (isWalkableBuildingKind(kind) || kind === 'torch' || kind === 'lantern') return [];
  const drawn = BUILDING_DRAWN_TILES[kind];
  if (!drawn) return [[tx, ty]];
  const [w, h] = drawn;
  const rows = h >= TWO_ROW_HEIGHT_TILES ? 2 : 1;
  const out: [number, number][] = [];
  // Sprite centre sits at 0.5 within column `tx`; column k (offset from tx) spans
  // [k, k+1) in those units.
  const left = 0.5 - w / 2, right = 0.5 + w / 2;
  const span = Math.ceil(w);
  for (let y = ty - (rows - 1); y <= ty; y++) {
    for (let k = -span; k <= span; k++) {
      if (Math.min(k + 1, right) - Math.max(k, left) >= 0.5) out.push([tx + k, y]);
    }
  }
  return out;
}

export function buildCollision(c: FarmCtx) {
  if (!c.world) { c.blockedGrid = null; return; }
  const { cols, rows } = c.world;
  const g = new Uint8Array(cols * rows);
  for (let i = 0; i < g.length; i++) if (c.world.terrain[i] === 'water') g[i] = 1;
  const inb = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows;
  const rect = (cx: number, cy: number, hw: number, y0: number, y1: number) => {
    for (let y = cy + y0; y <= cy + y1; y++)
      for (let x = cx - hw; x <= cx + hw; x++)
        if (inb(x, y)) g[y * cols + x] = 1;
  };
  // Solid objects block their base tile so the hero walks UP TO them (a felled
  // tree's tile stays blocked, so stumps block too). Small scatter (grass,
  // flowers, mushrooms) stays walkable. Block 6: hills are impassable cliffs —
  // hillocks + boulders block their tile, mountains a wider base. Dungeon
  // entrances stay walkable (you stand on one to descend).
  // §4: bushes are no longer solid (tiny/no collider). A tree that has been
  // uprooted (stump grubbed out → isCleared) unblocks its tile so it can be
  // walked over AND tilled/built; a standing tree or un-grubbed stump still blocks.
  const solid = new Set<Decor['kind']>(['tree', 'boulder', 'ore_rock', 'hillock']);
  for (const d of c.world.decor) {
    if (d.kind === 'mountain') rect(d.tx, d.ty, 1, 0, 0);
    else if (d.kind === 'tree' && isCleared(c, d)) continue;   // uprooted → tile freed
    else if (solid.has(d.kind) && inb(d.tx, d.ty)) g[d.ty * cols + d.tx] = 1;
  }

  // Placed STRUCTURES block: the hero cannot walk through his own house, stations
  // or fences. A fence that you can stroll through is not a fence — it has to be a
  // real boundary or penning animals and keeping night mobs off the crops means
  // nothing.
  //
  // The obvious hazard is fencing yourself in with no gate, which reads to a player
  // as "movement is broken". The answer is not to make fences passable (tried that;
  // it made them decorative) but to give the player a way THROUGH: swing the axe at
  // a wooden fence and it comes down (see `chopFenceAt` in resources.ts). So a wall
  // is always a wall, and always removable by the tool that built the wood.
  //
  // A torch and a lantern are thin posts scattered in numbers, so they stay walkable.
  // The house is a multi-tile cottage drawn ~2 tiles wide and 2 tall from its anchor,
  // so it blocks that footprint rather than the single anchor tile.
  // Roads/paths/bridge decks are ground you WALK ON — never a collider (see
  // map/decor-kinds.ts, the shared classification the renderer reads too).
  for (const b of c.farm?.buildings ?? []) {
    if (isWalkableBuildingKind(b.kind)) {
      // A finished deck/road CLEARS its tile — that is the whole point of a bridge:
      // the water underneath was blocked above, and `continue` alone would leave it
      // blocked. Scaffolding grants nothing: you cannot cross a bridge being built.
      if (b.isReady !== false && inb(b.tx, b.ty)) g[b.ty * cols + b.tx] = 0;
      continue;
    }
    for (const [x, y] of solidBuildingTiles(b.kind, b.tx, b.ty)) if (inb(x, y)) g[y * cols + x] = 1;
  }

  c.blockedGrid = g;
  // Anything cached against the old layout is now a lie. Bumping the version
  // invalidates the path cache wholesale and tells anyone mid-walk to re-plan
  // (see the re-route in the ticker) — which is what makes an obstacle that
  // *appears* (a mob, a building) actually redirect a walk already in progress.
  c.gridVersion++;
  c.pathCache.clear();
  // The layout just changed under everyone's feet — including the hero's. If his
  // own cell became solid he can no longer move at all (see unstickPlayer).
  unstickPlayer(c);
}

// Tiles NO wild decor may render on: every plot cell PLUS its 8 neighbours. The plot's
// sand-lip "skirt" frame is drawn on those neighbour cells and already contains its own
// grass, so a world grass-tuft/flower on the same cell shows as a lighter-green patch
// UNDER the skirt (owner: "grass under the skirt of the grass"), and a tree/bush
// anchored there grows out of the plot's border. Hiding the whole plot+ring set removes
// both — the bed and its frame own those cells.
export function plotHideSet(c: FarmCtx): Set<string> {
  const s = new Set<string>();
  if (!c.farm) return s;
  for (const p of c.farm.plots)
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) s.add(`${p.tx + dx},${p.ty + dy}`);
  return s;
}

/**
 * Decor kinds a tilled bed is allowed to hide. Scenery only — never an NPC.
 *
 * NPCs are decor entities too, and once `makeDecorSprite` started giving the
 * oracle kind a `.decor` back-reference (so the talk pointer can find Fin), the
 * hide loop below started matching them: tilling any tile within one of the
 * grove oracle or Fisherman Fin turned the NPC invisible AND eventMode 'none',
 * and `oracleUnderPointer` skips `!s.visible`, so the quest-giver went mute
 * until the sprite happened to be culled and rebuilt.
 */
export const hidesUnderPlots = (kind: string): boolean => kind !== 'oracle';

// Ambient scatter (grass tufts, flowers, mushrooms) and tall props are hidden under a
// tilled plot AND its sand-lip skirt so nothing wild pokes through the bed or its frame.
// Re-shows if the plot is later removed. Called from paintPlots.
export function clearDecorUnderPlots(c: FarmCtx) {
  if (!c.farm) return;
  const hide = plotHideSet(c);
  for (const { s, d } of c.activeSmall.values()) {
    const under = hide.has(`${d.tx},${d.ty}`);
    if (s.visible === under) s.visible = !under;
  }
  // Tall props (tree/bush/mushroom/rock) are also made non-interactive — a mushroom
  // regrowing on a bed/skirt could otherwise be gathered. (One-way: a legitimately
  // cleared/gathered prop stays hidden on its own; a removed plot re-shows its decor
  // when cullTall recreates the sprite on the next pass.)
  for (const s of c.activeTall.values()) {
    const d = (s as unknown as { decor?: Decor }).decor;
    if (d && hidesUnderPlots(d.kind) && hide.has(`${d.tx},${d.ty}`)) { s.visible = false; s.eventMode = 'none'; }
  }
}

// Small scatter (grass/flowers) viewport cull — mirrors cullTall but into the flat
// smallLayer. Scatter under a tilled plot is hidden on spawn.
export function cullSmall(c: FarmCtx) {
  if (!c.smallLayer || !c.cam || !c.app || !c.world) return;
  const sc = c.cam.getScale();
  const x0 = -c.cam.world.x / sc, y0 = -c.cam.world.y / sc;
  const left = Math.floor(x0 / TILE) - CULL_MARGIN;
  const top = Math.floor(y0 / TILE) - CULL_MARGIN;
  const right = Math.ceil((x0 + c.app.screen.width / sc) / TILE) + CULL_MARGIN;
  const bottom = Math.ceil((y0 + c.app.screen.height / sc) / TILE) + CULL_MARGIN;
  const inView = (d: Decor) => d.tx >= left && d.tx <= right && d.ty >= top && d.ty <= bottom;
  const hide = plotHideSet(c);   // beds + their sand-lip skirt — no scatter pokes through

  const bx0 = Math.floor(left / BUCKET), bx1 = Math.floor(right / BUCKET);
  const by0 = Math.floor(top / BUCKET), by1 = Math.floor(bottom / BUCKET);
  const wanted = new Set<string>();
  for (let bx = bx0; bx <= bx1; bx++) {
    for (let by = by0; by <= by1; by++) {
      const list = c.smallBuckets.get(`${bx},${by}`);
      if (!list) continue;
      for (const d of list) {
        if (!inView(d)) continue;
        wanted.add(d.id);
        if (!c.activeSmall.has(d.id)) {
          const s = makeDecorSprite(c, d);
          // Cleared ground cover stays gone until it grows back — otherwise walking
          // away and back would respawn every tuft the player just pulled up.
          if (hide.has(`${d.tx},${d.ty}`) || isGathered(c, d)) s.visible = false;
          c.activeSmall.set(d.id, { s, d });
          c.smallLayer.addChild(s);
        }
      }
    }
  }
  let killed = false;
  for (const [id, v] of c.activeSmall) {
    if (!wanted.has(id)) { v.s.destroy(); c.activeSmall.delete(id); killed = true; }
  }
  if (killed) pruneDeadAnim(c);   // grass/flowers register on the shared ticker
}

const TERRAIN_CHUNK = 24;   // tiles per terrain/coastline chunk

// Build one terrain+coastline chunk (bounded run-length terrain + shore sprites).
function buildTerrainChunk(c: FarmCtx, cx: number, cy: number): { ground: Container; shore: Container; anim: AnimWater[] } {
  const world = c.world!, tex = c.tex!;
  const tx0 = cx * TERRAIN_CHUNK, ty0 = cy * TERRAIN_CHUNK;
  const tx1 = Math.min(world.cols, tx0 + TERRAIN_CHUNK), ty1 = Math.min(world.rows, ty0 + TERRAIN_CHUNK);
  const ground = new Container(); ground.sortableChildren = true;
  const shore = new Container();
  const anim: AnimWater[] = [];
  const isLand = (tx: number, ty: number) => tx >= 0 && ty >= 0 && tx < world.cols && ty < world.rows && world.terrain[ty * world.cols + tx] !== 'water';
  for (let y = ty0; y < ty1; y++) {
    // Terrain: run-length within this chunk's column range.
    let x = tx0;
    while (x < tx1) {
      const t = world.terrain[y * world.cols + x];
      if (t === 'water') { x++; continue; }
      // Split runs by BIOME too, so a volcano stretch tiles the scorched ground.
      const vol = world.biome?.[y * world.cols + x] === BIOME_VOLCANO;
      let x2 = x + 1;
      while (x2 < tx1 && world.terrain[y * world.cols + x2] === t
        && (world.biome?.[y * world.cols + x2] === BIOME_VOLCANO) === vol) x2++;
      const s = terrainRun(c, t, x2 - x, x, y, vol);
      s.x = x * TILE; s.y = y * TILE; s.zIndex = -1;
      ground.addChild(s);
      x = x2;
    }
    // Coastline: animated waterline sprites for shore-water tiles in this chunk.
    for (let sx = tx0; sx < tx1; sx++) {
      if (world.terrain[y * world.cols + sx] !== 'water') continue;
      const keys = shoreKeys(
        isLand(sx, y - 1), isLand(sx, y + 1), isLand(sx - 1, y), isLand(sx + 1, y),
        isLand(sx - 1, y - 1), isLand(sx + 1, y - 1), isLand(sx - 1, y + 1), isLand(sx + 1, y + 1),
      );
      if (!keys.length) continue;
      const variant = (((sx * 73856093) ^ (y * 19349663)) % 100 < 25) ? 'stone' as const : 'grass' as const;
      for (const key of keys) {
        const frames = tex.shore[variant][key];
        const s = new Sprite(frames[0]); s.scale.set(PX);
        s.x = sx * TILE; s.y = y * TILE;
        const i = (sx * 7 + y * 13) % frames.length; s.texture = frames[i];
        shore.addChild(s);
        anim.push({ sprite: s, frames, fps: 6, t: 0, i });
      }
    }
  }
  ground.enableRenderGroup?.();
  return { ground, shore, anim };
}

// Viewport-window the terrain+coastline: build chunks that come into view, destroy
// those that leave (and remove their shore-animation entries). Keeps only the
// on-screen slice of the 683×464 map resident instead of the whole thing.
export function cullTerrain(c: FarmCtx) {
  if (!c.scenery || !c.waterFxLayer || !c.cam || !c.app || !c.world || !c.tex) return;
  const sc = c.cam.getScale();
  const x0 = -c.cam.world.x / sc, y0 = -c.cam.world.y / sc;
  const left = Math.floor(x0 / TILE) - CULL_MARGIN;
  const top = Math.floor(y0 / TILE) - CULL_MARGIN;
  const right = Math.ceil((x0 + c.app.screen.width / sc) / TILE) + CULL_MARGIN;
  const bottom = Math.ceil((y0 + c.app.screen.height / sc) / TILE) + CULL_MARGIN;
  const cx0 = Math.max(0, Math.floor(left / TERRAIN_CHUNK)), cx1 = Math.floor(right / TERRAIN_CHUNK);
  const cy0 = Math.max(0, Math.floor(top / TERRAIN_CHUNK)), cy1 = Math.floor(bottom / TERRAIN_CHUNK);
  const wanted = new Set<string>();
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      const key = `${cx},${cy}`;
      wanted.add(key);
      if (!c.terrainChunks.has(key)) {
        const chunk = buildTerrainChunk(c, cx, cy);
        c.terrainChunks.set(key, chunk);
        c.scenery.addChild(chunk.ground);
        c.waterFxLayer.addChild(chunk.shore);
        if (chunk.anim.length) c.animWater.push(...chunk.anim);
      }
    }
  }
  for (const [key, v] of c.terrainChunks) {
    if (wanted.has(key)) continue;
    if (v.anim.length) { const gone = new Set(v.anim); c.animWater = c.animWater.filter((a) => !gone.has(a)); }
    v.ground.destroy(); v.shore.destroy();
    c.terrainChunks.delete(key);
  }
}

export const isBlockedPx = (c: FarmCtx, wx: number, wy: number): boolean => {
  if (!c.blockedGrid || !c.world) return false;
  const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);
  if (tx < 0 || ty < 0 || tx >= c.world.cols || ty >= c.world.rows) return true;
  return c.blockedGrid[ty * c.world.cols + tx] === 1;
};

/** True when the world tile at these pixels is open water — used by water birds,
 *  which swim on water and treat land as "blocked". */
export const isWaterPx = (c: FarmCtx, wx: number, wy: number): boolean => {
  if (!c.world) return false;
  const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);
  if (tx < 0 || ty < 0 || tx >= c.world.cols || ty >= c.world.rows) return false;
  return c.world.terrain[ty * c.world.cols + tx] === 'water';
};

// ─── Pathfinding (map/nav.ts): direct line → A* shortest → greedy partial ─────
// One navigation module for every walking agent — the hero here, NPCs/bots
// later. It picks the cheapest adequate strategy per click and string-pulls
// the result into smooth diagonal runs.
export const tileBlocked = (c: FarmCtx, tx: number, ty: number): boolean =>
  !c.blockedGrid || !c.world || tx < 0 || ty < 0 || tx >= c.world.cols || ty >= c.world.rows || c.blockedGrid[ty * c.world.cols + tx] === 1;

export const navGrid = (c: FarmCtx): NavGrid => ({ cols: c.world!.cols, rows: c.world!.rows, blocked: (tx, ty) => tileBlocked(c, tx, ty) });

export const nearestOpenTile = (c: FarmCtx, tx: number, ty: number): [number, number] | null =>
  c.world ? nearestOpen(navGrid(c), tx, ty) : null;

const tileOf = (t: { tx: number; ty: number }): [number, number] => [t.tx, t.ty];

/**
 * Get the hero OUT of a solid cell.
 *
 * Movement only ever tests the DESTINATION tile (player.update), so a hero whose
 * own cell turned solid — a building dropped on top of him, a world/collision
 * rebuild that blocked his tile — has every candidate step rejected and freezes in
 * place; click-to-move cannot save him either, because A* has no open start tile.
 * Nothing else in the client detects that, so this is the recovery: shove him to
 * the nearest walkable tile (same nearestOpen the pathfinder uses) and drop any
 * walk in progress, since it was planned from a tile he no longer stands on.
 *
 * Called after every collision rebuild, which covers BOTH the moment of placement
 * and a fresh load — so a player already stuck right now is freed when he logs in.
 */
export function unstickPlayer(c: FarmCtx): boolean {
  const p = c.player;
  if (!p || !c.world || !c.blockedGrid) return false;
  // Feet space: collision is probed at y - FOOT_OFFSET, so the occupied tile must be too.
  const tx = Math.floor(p.x / TILE), ty = Math.floor((p.y - FOOT_OFFSET) / TILE);
  if (!tileBlocked(c, tx, ty)) return false;
  // nearestOpen gives up after a short radius, which is the right call for the
  // usual case (a building dropped on the hero — open ground is one step away)
  // and useless for the one that stranded players: dumped in the top-left sea,
  // there is no land within its reach and the rescue returned null, leaving the
  // hero standing in water unable to take a single step. pickSpawn searches the
  // whole map, so "nowhere near here" degrades to "back at the farm" instead of
  // to "stuck forever".
  const open = nearestOpenTile(c, tx, ty) ?? tileOf(pickSpawn(c.world, c.blockedGrid));
  p.setPath([]);                                 // also zeroes the key vector
  p.setPos((open[0] + 0.5) * TILE, (open[1] + 0.5) * TILE + FOOT_OFFSET);
  return true;
}

// IMPORTANT: collision probes the hero's FEET (player.update tests
// y - FOOT_OFFSET), so routing must live in feet space too — start tile from
// the feet position and waypoints that put the FEET at tile centers. Mixing
// spaces put the feet one row into tree tiles and ground him on trunks.
// Routes are cached per (start tile → goal tile) for the life of a collision
// layout. Re-clicking the same destination, or a queued walk-to-object that
// re-plans from the same tile, is common and an A* sweep of a 683-wide map is
// not free. The cache dies with the layout, so it can never route through an
// obstacle that has since appeared.

/** Plan a walk to a tile and remember it, so it can be re-planned if the map changes. */
export function walkTo(c: FarmCtx, goalTx: number, goalTy: number): boolean {
  if (!c.player) return false;
  const p = findPath(c, c.player.x, c.player.y, goalTx, goalTy);
  if (p && p.length) {
    c.player.setPath(p);
    c.walkGoal = { tx: goalTx, ty: goalTy, v: c.gridVersion };
    return true;
  }
  c.walkGoal = null;
  return false;
}

export function findPath(c: FarmCtx, startPx: number, startPy: number, goalTx: number, goalTy: number): { x: number; y: number }[] | null {
  if (!c.world || !c.blockedGrid) return null;
  const sTx = Math.floor(startPx / TILE), sTy = Math.floor((startPy - FOOT_OFFSET) / TILE);
  const key = `${sTx},${sTy}>${goalTx},${goalTy}`;
  if (c.pathCache.has(key)) {
    const hit = c.pathCache.get(key)!;
    return hit ? hit.map((p) => ({ ...p })) : null;   // copy: the caller shifts waypoints off it
  }
  const route = findRoute(navGrid(c), sTx, sTy, goalTx, goalTy);
  const pts = !route.tiles.length
    ? (route.complete ? [] : null)
    // A partial route still walks the hero as close as possible to the goal.
    : route.tiles.map(([tx, ty]) => ({ x: (tx + 0.5) * TILE, y: (ty + 0.5) * TILE + FOOT_OFFSET }));
  if (c.pathCache.size >= PATH_CACHE_MAX) {
    const oldest = c.pathCache.keys().next().value as string | undefined;
    if (oldest !== undefined) c.pathCache.delete(oldest);
  }
  c.pathCache.set(key, pts);
  return pts ? pts.map((p) => ({ ...p })) : pts;
}

// A shore-water FX sprite; animated kinds (ripple/fish) are registered so the
// ticker advances their frames.
export function makeWaterSprite(c: FarmCtx, d: Decor): Sprite {
  const s = new Sprite();
  s.scale.set(PX);
  if (d.kind === 'reed') {
    s.texture = c.tex!.decor.grass_tuft[0];
    s.anchor.set(0.5, 1);
    s.alpha = 0.9;
    s.x = (d.tx + 0.5 + d.ox) * TILE; s.y = (d.ty + 1 + d.oy) * TILE;
  } else {
    s.anchor.set(0.5, 0.5);
    s.x = (d.tx + 0.5 + d.ox) * TILE; s.y = (d.ty + 0.5 + d.oy) * TILE;
    if (d.kind === 'water_stone') {
      s.texture = c.tex!.water.stone[Math.min(d.variant, c.tex!.water.stone.length - 1)];
    } else {
      const frames = d.kind === 'fish' ? c.tex!.water.fish
        : d.kind === 'wave' ? c.tex!.water.wave
          : c.tex!.water.ripple;
      if (d.kind === 'fish') s.alpha = 0.75; // fish read as shapes below the surface
      if (d.kind === 'wave') s.alpha = 0.85;
      // Stagger start frame AND rate: a shared frame count and a shared fps is
      // what makes scattered water read as one blinking grid rather than
      // weather. The jitter is positional, so it stays stable across rebuilds.
      const i = Math.floor((d.tx * 7 + d.ty * 13) % frames.length);
      s.texture = frames[i];
      const baseFps = d.kind === 'fish' ? 9 : d.kind === 'wave' ? 5 : 7;
      const fps = baseFps * (0.7 + ((d.tx * 31 + d.ty * 17) % 7) / 10);
      c.animWater.push({ sprite: s, frames, fps, t: 0, i });
    }
  }
  return s;
}

/**
 * Move regrown nodes somewhere new.
 *
 * Run once per frame BEFORE culling. A node whose regrow timer has just elapsed is
 * re-seeded on a free tile nearby instead of popping back onto the tile it was
 * taken from — see map/regrow-relocate.ts for why. Cleared land stays cleared and
 * the forest slowly moves as it is worked.
 *
 * Done as a pre-pass rather than inside makeDecorSprite because relocating changes
 * which BUCKET a decor lives in, and the culler is iterating those bucket lists
 * when it builds sprites — mutating them mid-iteration is how you drop nodes.
 */
/** "5h 20m" / "12m" / "40s" — a wait a player can plan around, not a millisecond count. */
export function fmtWait(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Re-draw planted acorns that have just matured.
 *
 * Growth is 6h and a tab stays open far longer than that, so a seedling whose
 * deadline passes mid-session must become a real tree without a reload. Dropping
 * the sprite is enough — the culler rebuilds it from the same decor object, and
 * makeDecorSprite reads the deadline again and picks the grown texture.
 *
 * The entry is then DELETED from `world.saplings`. Keeping it re-ran this sweep on
 * every frame: cullTall rebuilds any in-view decor missing from activeTall in the
 * same frame, so destroy -> makeDecorSprite -> destroy churned a full Pixi teardown
 * per grown tree at 60fps. Nothing needs the entry once the deadline has passed —
 * isYoungSapling already answers false for a missing id, and buildScene re-hydrates
 * the map from the farm snapshot on every view rebuild.
 */
export function sweepSaplings(c: FarmCtx) {
  const map = c.world?.saplings;
  if (!map?.size) return;
  const now = Date.now();
  for (const [id, readyAt] of map) {
    if (!saplingGrown(readyAt, now)) continue;
    const s = c.activeTall.get(id);
    if (s) {                             // off screen: it will be built grown when it culls in
      s.destroy();
      c.activeTall.delete(id);
    }
    map.delete(id);
  }
}

export function sweepRegrowth(c: FarmCtx) {
  if (!c.world) return;
  sweepSaplings(c);
  const now = Date.now();
  // Only the three timer maps can hold a node that is due back; scanning them is
  // O(harvested), not O(world decor).
  const due: { d: Decor; map: Map<string, number> }[] = [];
  const scan = (map: Map<string, number>) => {
    for (const [id, until] of map) {
      if (until > now) continue;
      const d = decorById(c).get(id);
      if (d) due.push({ d, map });
      else map.delete(id);
    }
  };
  scan(c.felledUntil); scan(c.minedUntil); scan(c.gatheredUntil);
  if (!due.length) return;

  const world: RelocateWorld = {
    blocked: (tx, ty) => tileBlocked(c, tx, ty) || occupied(c, tx, ty),
    reindex: (d, fromTx, fromTy) => {
      const buckets = TALL_KINDS.has(d.kind) ? c.tallBuckets : c.smallBuckets;
      const from = buckets.get(bkey(fromTx, fromTy));
      if (from) {
        const i = from.indexOf(d);
        if (i >= 0) from.splice(i, 1);
      }
      const k = bkey(d.tx, d.ty);
      const to = buckets.get(k);
      if (to) to.push(d); else buckets.set(k, [d]);
    },
  };

  let anyMoved = false;
  for (const { d, map } of due) {
    const moved = relocateRegrown(d, world);
    anyMoved ||= moved;
    map.delete(d.id);
    c.clearedStumps.delete(d.id);
    c.boulderHits.delete(d.id);
    // A regrown tree comes back as a SAPLING and stages up (treeStageTexture),
    // wherever it now stands — a full-grown tree blinking into a cleared patch is
    // exactly the "it is still there again" the relocation is meant to end.
    if (moved && d.kind === 'tree') c.treeGrowStart.set(d.id, now);
    // Drop the old sprite (hidden stump / empty tile) so the culler rebuilds the
    // node whole, at wherever it now stands.
    const active = TALL_KINDS.has(d.kind) ? c.activeTall : c.activeSmall;
    const s = active.get(d.id) as Sprite | { s: Sprite } | undefined;
    if (s) {
      ('destroy' in s ? s : s.s).destroy();
      active.delete(d.id);
    }
  }
  // The blocked grid is built from where decor STANDS, so moving a node without
  // rebuilding it left the vacated tile solid (an invisible wall on cleared
  // ground) and the node's new tile walk-through — until some unrelated build or
  // demolish happened to rebuild it. A regrown tree is an obstacle again; a
  // cleared one is not.
  if (anyMoved) buildCollision(c);
}

/** Lazy decor-by-id index, built once per world. */
let decorIndexFor: unknown = null;
let decorIndex: Map<string, Decor> = new Map();
function decorById(c: FarmCtx): Map<string, Decor> {
  if (decorIndexFor !== c.world) {
    decorIndexFor = c.world;
    decorIndex = new Map(c.world!.decor.map((d) => [d.id, d]));
  }
  return decorIndex;
}

/** A tile already carrying decor, a plot or a building cannot host a regrown node. */
function occupied(c: FarmCtx, tx: number, ty: number): boolean {
  // The bed SKIRT counts as taken, not just the bed. Tall decor on a skirt cell is
  // hidden and click-dead (clearDecorUnderPlots) while collision still marks it
  // solid, so a tree regrowing there became a permanent invisible wall beside the
  // beds — and world gen forbids the yard for exactly the same reason.
  if (plotHideSet(c).has(`${tx},${ty}`)) return true;
  if (c.farm?.plots.some((p) => p.tx === tx && p.ty === ty)) return true;
  if (c.farm?.buildings.some((b) => b.tx === tx && b.ty === ty)) return true;
  const k = bkey(tx, ty);
  const hit = (list?: Decor[]) => !!list?.some((e) => e.tx === tx && e.ty === ty);
  return hit(c.tallBuckets.get(k)) || hit(c.smallBuckets.get(k));
}


/**
 * Nearest bed for the farmer to fuss over, in world px.
 *
 * A DRY planted bed wins over a watered one, so the villager you see kneeling in
 * the field is at the bed his standing order would actually water next. When
 * everything is watered he still tends the nearest planted bed rather than
 * standing idle — the loop is a performance (see villagers.ts) and a farmhand
 * with nothing to do should look busy, not broken.
 */
function nearestPlotNode(c: FarmCtx, wx: number, wy: number, radiusTiles: number) {
  const maxPx = radiusTiles * TILE;
  let best: { x: number; y: number } | null = null;
  let bestScore = Infinity;
  for (const p of c.farm?.plots ?? []) {
    if (!p.cropId) continue;                        // bare tilled earth has nothing to tend
    const x = (p.tx + 0.5) * TILE, y = (p.ty + 1) * TILE;
    const dist = Math.hypot(x - wx, y - wy);
    if (dist > maxPx) continue;
    // Dry beds sort ahead of watered ones by a margin wider than the search area,
    // so proximity only ever breaks ties WITHIN a wetness class.
    const score = dist + (p.watered ? maxPx * 2 : 0);
    if (score < bestScore) { bestScore = score; best = { x, y }; }
  }
  return best;
}

/**
 * Nearest workable node of the given decor kinds, for a villager's work loop.
 *
 * Searches the coarse decor buckets outward from (wx, wy) rather than the whole
 * world list — there are tens of thousands of decor entries and this runs every
 * time a villager picks a new job, once every few seconds per villager.
 *
 * Skips anything already harvested (a felled tree, a mined vein) so a forester
 * does not stand swinging at a stump, and takes the CLOSEST match so villagers
 * work the trees by the hut instead of setting off across the map.
 */
export function findWorkNode(c: FarmCtx, kinds: string[], wx: number, wy: number, radiusTiles: number) {
  // Two trades work things that are not decor at all, so they are matched first
  // against their own source: the farmer works PLOTS (server rows on c.farm) and
  // the herder works PENNED ANIMALS (live entities in the livestock layer).
  // Neither ever appears in the decor buckets the scan below walks.
  if (kinds.includes(PLOT_NODE)) return nearestPlotNode(c, wx, wy, radiusTiles);
  if (kinds.includes(PEN_ANIMAL_NODE)) return findPenAnimal(c, wx, wy, radiusTiles);
  if (!c.world) return null;
  const want = new Set(kinds);
  const ctx = Math.floor(wx / TILE), cty = Math.floor(wy / TILE);
  const br = Math.ceil(radiusTiles / BUCKET);
  const bx0 = Math.floor(ctx / BUCKET) - br, bx1 = Math.floor(ctx / BUCKET) + br;
  const by0 = Math.floor(cty / BUCKET) - br, by1 = Math.floor(cty / BUCKET) + br;
  const maxPx = radiusTiles * TILE;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (let bx = bx0; bx <= bx1; bx++) {
    for (let by = by0; by <= by1; by++) {
      for (const list of [c.tallBuckets.get(`${bx},${by}`), c.smallBuckets.get(`${bx},${by}`)]) {
        if (!list) continue;
        for (const d of list) {
          if (!want.has(d.kind)) continue;
          if (isFelled(c, d) || isMined(c, d) || isGathered(c, d)) continue;
          const x = (d.tx + 0.5) * TILE, y = (d.ty + 1) * TILE;
          const dist = Math.hypot(x - wx, y - wy);
          if (dist <= maxPx && dist < bestD) { bestD = dist; best = { x, y }; }
        }
      }
    }
  }
  return best;
}

/**
 * Give a decor sprite its pack animation, if its kind has one.
 *
 * The pack's Outdoor_Decor_Animations were unused: mushrooms and the grove
 * fountain were baked stills. (Grass and wildflowers stay stills — the pack has
 * no animation of that art; see DECOR_ANIM.) They are registered on the ONE
 * shared ticker this game has (c.animWater, driven from views/farm.ts) — a second
 * ticker would double the per-frame walk over the same sprites — and that ticker
 * only advances entries inside the hero's vision, which is what makes animating
 * scatter affordable at all: the culler keeps ~a screenful of these alive, not
 * the map's tens of thousands.
 *
 * Returns true if an entry was registered (the caller then knows the sprite's
 * texture is an animation frame, not the still).
 */
function attachDecorAnim(c: FarmCtx, d: Decor, s: Sprite): boolean {
  if (!hasDecorAnim(d)) return false;
  const frames = c.tex?.decorAnim[d.kind]?.[d.variant];
  if (!frames?.length || !frames[0]) return false;
  const spec = DECOR_ANIM[d.kind];
  const i = animStartFrame(d, frames.length);
  s.texture = frames[i];
  // The animated fountain is a different (taller) fountain in the same pack set,
  // so it needs its own scale to keep the shrine drawn at the size it is now —
  // see FOUNTAIN_ANIM_SCALE.
  if (d.kind === 'fountain') s.scale.set(FOUNTAIN_ANIM_SCALE);
  c.animWater.push({ sprite: s, frames, fps: animFps(d, spec.fps), t: 0, i });
  return true;
}

/**
 * Drop ticker entries whose sprite the culler just destroyed.
 *
 * Scatter is created and destroyed every time the player walks a screen's width,
 * so without this the ticker list grows without bound and would advance textures
 * on destroyed sprites. Same guard paintPlots uses for placed lights.
 */
function pruneDeadAnim(c: FarmCtx) {
  if (c.animWater.length) c.animWater = c.animWater.filter((a) => !a.sprite.destroyed);
}

export function makeDecorSprite(c: FarmCtx, d: Decor): Sprite {
  // A tree/rock whose regrow timer has elapsed is whole again — drop stale state.
  if (d.kind === 'tree' && !isFelled(c, d)) { c.felledUntil.delete(d.id); c.clearedStumps.delete(d.id); }
  if (d.kind === 'ore_rock' && !isMined(c, d)) c.minedUntil.delete(d.id);
  const felled = isFelled(c, d);
  // A planted acorn draws as the pack's seedling until its deadline passes, then as
  // the ordinary small oak `decorTexture` already returns for this variant — the
  // same decor object throughout, so it y-sorts, culls and blocks like any tree.
  const young = d.kind === 'tree' && isYoungSapling(c, d);
  const s = new Sprite(felled ? stumpFor(c, d)
    : young ? c.tex!.tree[TREE_KINDS[d.variant >> 1] ?? 'oak'].sapling
    : decorTexture(c.tex!, d));
  s.anchor.set(0.5, 1);
  s.scale.set(felled || young ? PX : decorScale(d));
  // Fence: stretch each segment to EXACTLY one tile wide so contiguous posts join
  // into a continuous rail (the pack piece is a seamless 16px unit). Height keeps
  // the normal pixel scale so posts don't tower.
  if (d.kind === 'fence' && !felled) s.scale.set(TILE / decorTexture(c.tex!, d).frame.width, PX);
  // A felled tree / a seedling is drawing its own state sprite — never animate over it.
  if (!felled && !young) attachDecorAnim(c, d, s);
  s.x = (d.tx + 0.5 + d.ox) * TILE;
  s.y = (d.ty + 1 + d.oy) * TILE;
  s.zIndex = s.y;
  // NPCs carry their decor back-reference so the pointer can find them.
  //
  // input.ts `oracleUnderPointer` walks the live tall sprites and reads
  // `sprite.decor` — but only trees, gatherables and ore veins were ever given
  // one, so it could never match an NPC and the walk-up-and-talk never fired.
  // The grove oracle has been un-clickable for that reason; Fisherman Fin is the
  // same kind and would have shipped mute.
  if (d.kind === 'oracle') (s as unknown as { decor: Decor }).decor = d;
  if (d.kind === 'tree') {
    (s as unknown as { decor: Decor }).decor = d;   // for regrow lookup
    const cleared = c.clearedStumps.has(d.id) && felled;
    s.visible = !cleared;                            // grubbed-out stump: nothing to show till regrow
    s.cursor = AXE_CURSOR;                           // the pointer becomes an axe over a tree/stump
    s.eventMode = cleared ? 'none' : 'static';       // choppable while a tree OR a stump
    // Hit only the body — exclude the transparent area below the feet and the
    // soft shadow at its base. Sized to what's shown (tree or the small stump).
    const tf = (felled ? stumpFor(c, d) : young ? s.texture : decorTexture(c.tex!, d)).frame;
    s.hitArea = new Rectangle(-tf.width / 2, -tf.height, tf.width, tf.height * (felled || young ? 1 : 0.85));
    s.on('pointerover', () => { if (!isCleared(c, d) && !isYoungSapling(c, d)) s.tint = 0x9be6a0; }); // green = cuttable (tool auto-equips on tap)
    s.on('pointerout', () => { s.tint = 0xffffff; });
    // The `dragged` read here (and on the gather/ore props below) is only sound
    // because the camera resets it in the CAPTURE phase: this handler's
    // stopPropagation() means the stage's bubble-phase listener never runs for a
    // press that lands on a prop, so a bubble-phase reset left the flag stuck at
    // the previous gesture's value and every prop tap after a pan was swallowed.
    s.on('pointerdown', (e) => {
      e.stopPropagation();
      if (c.cam?.dragged) return;
      // A seedling is not a tree yet. Swinging an axe at it would spend energy on a
      // chop the server refuses (the Sapling row is not a tree), so the refusal is
      // spoken here — the same "say why, never swallow the tap" rule the hoe follows.
      if (isYoungSapling(c, d)) { toast(`Still growing 🌱 — ${fmtWait(saplingRemainingMs(c, d))} to go`); return; }
      autoSelectFor(c, d); requestOperate(c, d, s);
    });
    s.on('pointertap', (e) => e.stopPropagation());  // a tree tap heads the hero over — not a walk-to-point
  } else if (GATHER_KINDS.has(d.kind)) {
    if (!isGathered(c, d)) c.gatheredUntil.delete(d.id);   // respawned — drop stale state
    (s as unknown as { decor: Decor }).decor = d;
    if (isGathered(c, d)) { s.visible = false; s.eventMode = 'none'; }
    else {
      // A boulder is MINED, not picked: it wants the pickaxe cursor and the same
      // auto-equip every other tool-worked node gets. Without autoSelectFor the
      // hero swung whatever happened to be in hand at a rock that only answers to
      // a pick — which is the "I can't mine big stones" report.
      const big = d.kind === 'boulder';
      s.cursor = big ? PICK_CURSOR : 'pointer';
      s.eventMode = 'static';
      // From the sprite's CURRENT texture, not the still: an animated mushroom is
      // drawing a frame whose bbox is the union of its animation, and sizing the
      // hit box off the still would leave part of the cap untappable.
      const tf = s.texture.frame;
      s.hitArea = new Rectangle(-tf.width / 2 - 2, -tf.height - 2, tf.width + 4, tf.height + 4); // padded: tiny sprites
      s.on('pointerover', () => { if (!isGathered(c, d)) s.tint = big ? 0xffe08a : 0x9be6a0; }); // amber = minable, green = gatherable
      s.on('pointerout', () => { s.tint = 0xffffff; });
      // Auto-equip for EVERY prop that names an ideal tool, not just the big ones.
      // The `if (big)` guard meant a small stone kept whatever was in hand, which
      // is how the axe ended up swinging at it; `autoSelectFor` is already a no-op
      // for props with no ideal tool (mushrooms, bushes), so the guard bought
      // nothing.
      s.on('pointerdown', (e) => { e.stopPropagation(); if (!c.cam?.dragged) { autoSelectFor(c, d); requestOperate(c, d, s); } });
      s.on('pointertap', (e) => e.stopPropagation());
    }
  } else if (d.kind === 'ore_rock') {
    (s as unknown as { decor: Decor }).decor = d;
    if (isMined(c, d)) { s.visible = false; s.eventMode = 'none'; }
    else {
      s.cursor = PICK_CURSOR;                         // pickaxe — hold to mine stone/ore
      s.eventMode = 'static';
      const tf = decorTexture(c.tex!, d).frame;
      s.hitArea = new Rectangle(-tf.width / 2, -tf.height, tf.width, tf.height);
      s.on('pointerover', () => { if (!isMined(c, d)) s.tint = 0xffe08a; }); // amber = minable (pickaxe auto-equips on tap)
      s.on('pointerout', () => { s.tint = 0xffffff; });
      s.on('pointerdown', (e) => { e.stopPropagation(); if (!c.cam?.dragged) { autoSelectFor(c, d); requestOperate(c, d, s); } });
      s.on('pointertap', (e) => e.stopPropagation());
    }
  }
  return s;
}

// Keep only the tall decor within (viewport + margin) instantiated; sprites
// outside are destroyed. Bounds cost stays O(visible), not O(map).
export function cullTall(c: FarmCtx) {
  if (!c.entityLayer || !c.cam || !c.app || !c.world) return;
  const sc = c.cam.getScale();
  const x0 = -c.cam.world.x / sc, y0 = -c.cam.world.y / sc;
  const left = Math.floor(x0 / TILE) - CULL_MARGIN;
  const top = Math.floor(y0 / TILE) - CULL_MARGIN - 6; // extra above for tall canopies
  const right = Math.ceil((x0 + c.app.screen.width / sc) / TILE) + CULL_MARGIN;
  const bottom = Math.ceil((y0 + c.app.screen.height / sc) / TILE) + CULL_MARGIN;
  const inView = (d: Decor) => d.tx >= left && d.tx <= right && d.ty >= top && d.ty <= bottom;

  const bx0 = Math.floor(left / BUCKET), bx1 = Math.floor(right / BUCKET);
  const by0 = Math.floor(top / BUCKET), by1 = Math.floor(bottom / BUCKET);
  // Tall decor on a plot tile OR its sand-lip ring (a mushroom/bush that regrew under
  // a bed, or a tree the bed was tilled around) is hidden + made non-interactive:
  // nothing wild grows out of cultivated soil or its border.
  const hide = plotHideSet(c);
  const wanted = new Set<string>();
  for (let bx = bx0; bx <= bx1; bx++) {
    for (let by = by0; by <= by1; by++) {
      const list = c.tallBuckets.get(`${bx},${by}`);
      if (!list) continue;
      for (const d of list) {
        if (!inView(d)) continue;
        wanted.add(d.id);
        if (!c.activeTall.has(d.id)) {
          const s = makeDecorSprite(c, d);
          if (hide.has(`${d.tx},${d.ty}`)) { s.visible = false; s.eventMode = 'none'; }
          c.activeTall.set(d.id, s);
          c.entityLayer.addChild(s);
        }
      }
    }
  }
  let killed = false;
  for (const [id, s] of c.activeTall) {
    if (!wanted.has(id)) { s.destroy(); c.activeTall.delete(id); killed = true; }
  }
  if (killed) pruneDeadAnim(c);   // mushrooms/fountain register on the shared ticker
}

// Fade any tall object the player is standing behind (drawn in front of and
// overlapping the player) so the avatar stays visible.
export function updateOcclusion(c: FarmCtx) {
  if (!c.player) return;
  const px = c.player.x, py = c.player.y;
  for (const s of c.activeTall.values()) {
    const behind = s.y > py && s.y - py < s.height && Math.abs(s.x - px) < s.width / 2 + 4;
    const goal = behind ? FADE_ALPHA : 1;
    s.alpha += (goal - s.alpha) * 0.25; // ease for a soft fade
  }
}

/**
 * (Re)mount the villager layer: the hired villagers, plus one PINNED keeper per
 * finished Forager Hut.
 *
 * The keeper is not a hire and has no server row — he is the hut's shopkeeper,
 * drawn by the villager renderer (same forager strips, same y-sorting) so there
 * is exactly one NPC drawer in the codebase. He stands one tile in FRONT of the
 * hut: behind it he would be occluded by the 46×38 building sprite, which for the
 * player is indistinguishable from not being there.
 *
 * Exported because building or demolishing a hut must move him without a full
 * scene rebuild.
 */
export function mountVillagers(c: FarmCtx): void {
  c.villagers?.destroy();
  c.villagers = null;
  const keepers = (c.farm?.buildings ?? [])
    .filter((b) => b.kind === 'forage_hut' && b.isReady !== false)
    .map((b) => ({ kind: 'forager', tx: b.tx, ty: b.ty + 1, pinned: true, facing: 'down' as const }));
  void villageApi.get().then(async (st) => {
    const list = [...st.villagers, ...keepers];
    if (!c.alive || !c.entityLayer || !list.length) return;
    c.villagers = await createVillagers(list, c.entityLayer,
      (kinds, wx, wy, r) => findWorkNode(c, kinds, wx, wy, r));
  }).catch(async () => {
    // The village call is best-effort ambience — but the keeper is not: a hut you
    // built must have someone at it even if /village is down.
    if (!c.alive || !c.entityLayer || !keepers.length) return;
    c.villagers = await createVillagers(keepers, c.entityLayer);
  });
}
